import AddIcon from "@mui/icons-material/Add";
import { Grid, Button, Typography, MenuItem, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { FormDatePicker, FormTextField } from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import {
  AlertHelper,
  ColorHelper,
  DialogHelper,
  StringHelper,
  TableHelper,
  TimeHelper,
} from "helpers";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { I18nTranslator, translations } from "utils/i18n";
import {
  EInProcessCheckStatus,
  EInprocessCheckType,
  IApiResponse,
  IDownloadAllInprocessCheck,
  IDownloadInprocessCheck,
  IGetInProcessCheckRequest,
  IInProcessCheck,
} from "model";
import { usePaginationRequest, useSearchParams } from "hooks";
import { InProcessCheckApiService } from "services";
import { ProductCodeSelect } from "./InProgressCheckDetail/ProductCodeSelect";
import { ECommonDialogType } from "components/molecules/CommonDialog";
import { EAlertType } from "helpers/alertHelper";
import { INPROCESS_STATUS } from "common";
import ClearIcon from "@mui/icons-material/Clear";
import { theme } from "themes";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { ProductLotSelect } from "./InProgressCheckDetail/ProductLotSelect";
import { LoadingButton } from "@mui/lab";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { DownloadActions } from "redux/actions";
import { Download } from "@mui/icons-material";
import { Box } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import _ from "lodash";
import { Colors } from "utils";
import {
  canEditAfterReview,
  canPerformAndReview,
  canViewAndDownload,
} from "./InProgressCheckDetail/helper";

export const InProcessCheck = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams<IGetInProcessCheckRequest>();
  const [request, setRequest] = useState<IGetInProcessCheckRequest>({
    fromDate:
      searchParams.get("fromDate") || TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: searchParams.get("toDate") || TimeHelper.endOfWeek("MM-DD-YYYY"),
    productId: searchParams.get("productId") || "",
    productLot: searchParams.get("productLot") || "",
    pageNumber: Number.parseInt(searchParams.get("pageNumber") || "1"),
    pageSize: Number.parseInt(searchParams.get("pageSize") || "100"),
    orderBy: "createdDate desc",
    status: undefined,
  });
  const [refresh, setRefresh] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { loading, result } = usePaginationRequest<
    IGetInProcessCheckRequest,
    IInProcessCheck[]
  >(
    request,
    InProcessCheckApiService.getAllInProcessCheck,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const COLUMNS: GridColDef<IInProcessCheck>[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell<IInProcessCheck>({
      field: "action1",
      headerName: I18nTranslator(translations.masterData.action),
      align: "center",
      headerAlign: "center",
      size: 128,
      sortable: false,
      renderCell: ({ row }: any) => (
        <Box flex={1} display="flex" justifyContent="end">
          {row.status === EInProcessCheckStatus.Reviewed && (
            <IconButton
              onClick={() => downloadInprocessCheck(row)}
              disabled={!canViewAndDownload()}
            >
              <Download fontSize="small" />
            </IconButton>
          )}
          <IconButton
            onClick={() => history.push(`/inprocess-check/${row.id}`)}
            disabled={!canViewAndDownload()}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {(canEditAfterReview()
            ? true
            : row.status === EInProcessCheckStatus.Reviewed
            ? false
            : canPerformAndReview()) && (
            <IconButton onClick={() => onDeleteInProcessClick(row.id)}>
              <DeleteIcon
                sx={{ color: theme.colors.error.light }}
                fontSize="small"
              />
            </IconButton>
          )}
        </Box>
      ),
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "status",
      headerName: I18nTranslator(translations.report.status),
      renderCell: ({ value }) => (
        <Typography
          style={{
            backgroundColor: ColorHelper.getInProcessStatusColor(
              value as EInProcessCheckStatus
            ),
            padding: "3px 16px",
            borderRadius: "3px",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {StringHelper.getInProcessStatusName(value)}
        </Typography>
      ),
      size: "small",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "productCode",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.productCode
      ),
      size: "small",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "productName",
      headerName: I18nTranslator(translations.masterData.productName),
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "numberOfUnit",
      headerName: I18nTranslator(translations.report.packSize),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "totalQuantity",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.totalQuantity
      ),
      type: "number",
      size: "small",
      align: "right",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "totalPerformedQuantity",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.totalPerformedQuantity
      ),
      size: "small",
      align: "right",
      renderCell: ({ row }) => {
        if (row.type === EInprocessCheckType.TIME) {
          const color = !row.totalPerformedQuantity
            ? Colors.NEW
            : row.totalPerformedQuantity! < row.totalQuantity!
            ? Colors.IN_PROGRESS
            : Colors.COMPLETE;
          return (
            <Typography color={color} fontWeight="bold">{`${
              row.totalPerformedQuantity || 0
            }/${row.totalQuantity}`}</Typography>
          );
        } else {
          const color = !row.totalPerformedQuantity
            ? Colors.NEW
            : row.totalPerformedQuantity! < 3
            ? Colors.IN_PROGRESS
            : Colors.COMPLETE;
          return (
            <Typography color={color} fontWeight="bold">{`${
              row.totalPerformedQuantity || 0
            }/3`}</Typography>
          );
        }
      },
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "createdDate",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.createdDate
      ),
      renderCell: ({ row }) =>
        moment.utc(row.createdDate!.substring(0, 19)).local().format("L LT"),
      size: "small",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "reviewedTime",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.reviewDate
      ),
      renderCell: ({ row }) =>
        row.reviewedTime
          ? moment.utc(row.reviewedTime.substring(0, 19)).local().format("L LT")
          : null,
      size: "small",
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "reviewedBy",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.reviewedBy
      ),
    }),
    TableHelper.createCell<IInProcessCheck>({
      field: "reviewedNote",
      headerName: I18nTranslator(
        translations.inProcessCheckReportDetail.reviewedNote
      ),
    }),
  ];

  useEffect(() => {
    requestPermission();
  }, []);

  const onDeleteInProcessClick = (id: string) => {
    DialogHelper.open({
      title: I18nTranslator(translations.dialog.warning),
      message: I18nTranslator(
        translations.inProcessCheckReportDetail.deleteInProcessCheckConfirm
      ),
      type: ECommonDialogType.WARNING,
      onConfirm: () => deleteInProcessCheck(id),
      isCancel: true,
    });
  };

  const downloadAllInprocessCheck = () => {
    const params: IDownloadAllInprocessCheck = {
      fileName: `Inprocess_check_${moment(request.fromDate).format(
        "DD_MM_YYYY"
      )}_to_${moment(request.toDate).format("DD_MM_YYYY")}${
        request.productLot ? `_Lot_${request.productLot}_` : ""
      }${request.productCode ? `_Product_${request.productCode}_` : ""}${
        request.productName ? `_${request.productName}_` : ""
      }${
        !_.isNil(request.status)
          ? `_Status_${StringHelper.getInProcessStatusName(
              request.status,
              "en"
            )}`
          : ""
      }`,
      ...request,
    };
    dispatch(DownloadActions.downloadAllInprocessCheck.request(params));
  };

  const downloadInprocessCheck = (inprocessCheck: IInProcessCheck) => {
    const params: IDownloadInprocessCheck = {
      id: inprocessCheck.id!,
      fileName: `Inprocess check ${
        inprocessCheck.productCode
      } reviewed on ${moment
        .utc(inprocessCheck.reviewedTime)
        .format("DD/MM/YYYY_HH:mm")}`,
    };
    dispatch(DownloadActions.downloadInprocessCheck.request(params));
  };
  const requestPermission = () => {
    if (!("Notification" in window)) {
      DialogHelper.open({
        title: I18nTranslator(translations.dialog.warning),
        message: I18nTranslator(
          translations.inProcessCheckReportDetail.notiNotSupport
        ),
        type: ECommonDialogType.WARNING,
      });
    } else if (Notification.permission === "granted") {
    } else if (Notification.permission !== "denied") {
      DialogHelper.open({
        title: I18nTranslator(translations.dialog.warning),
        message: I18nTranslator(
          translations.inProcessCheckReportDetail.notiRequestPermission
        ),
        type: ECommonDialogType.WARNING,
        onConfirm: () => {
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              AlertHelper.showAlert(
                I18nTranslator(
                  translations.inProcessCheckReportDetail.notiGranted
                ),
                EAlertType.SUCCESS
              );
            } else {
              DialogHelper.open({
                title: I18nTranslator(translations.dialog.warning),
                message: I18nTranslator(
                  translations.inProcessCheckReportDetail.notiDenied
                ),
                type: ECommonDialogType.WARNING,
              });
            }
          });
        },
      });
    }
  };

  const deleteInProcessCheck = async (id: string) => {
    try {
      setIsLoading(true);
      const result = (await InProcessCheckApiService.deleteInProcessCheck(
        id
      )) as IApiResponse<string>;
      if (result.succeeded) {
        AlertHelper.showAlert(
          I18nTranslator(
            translations.inProcessCheckReportDetail.inProcessCheckDeletedSuccess
          ),
          EAlertType.SUCCESS
        );
        setRefresh(true);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
      console.error("Network error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <InProcessReportHeader
        request={request}
        onSubmit={(value) => {
          setRequest((prev) => ({
            ...prev,
            ...value,
            productId: value.productId,
          }));
          searchParams.setValues({
            fromDate: value.fromDate as string,
            toDate: value.toDate as string,
            productId: value.productId || "",
            productLot: value.productLot || "",
            pageNumber: value.pageNumber?.toString() || "",
            pageSize: value.pageSize?.toString() || "",
            // productCode: value.productCode?.toString() || "",
            status: value.status?.toString() || "",
          });
        }}
        onDownloadClick={downloadAllInprocessCheck}
      />
      <AppTable<IInProcessCheck>
        loading={loading || isLoading}
        data={result}
        columns={COLUMNS}
        onSearch={(searchText) => {}}
        // searchPlaceholder={"Tìm kiếm: Mã pallet"}
        onChangePage={(pageNumber) =>
          setRequest((prev) => ({ ...prev, pageNumber: pageNumber }))
        }
        hideHeader
        hideSearch
        removeActionColumn
        defaultHeaderTable={170}
        onPageSizeChange={(pageSize) =>
          setRequest((prev) => ({ ...prev, pageSize: pageSize }))
        }
        onRowPressEdit={(row) => history.push(`/inprocess-check/${row.id}`)}
        onRowPressDelete={(row) => onDeleteInProcessClick(row.id!)}
      />
    </>
  );
};

const InProcessReportHeader = (props: {
  request: IGetInProcessCheckRequest;
  onSubmit: (value: IGetInProcessCheckRequest) => void;
  onDownloadClick: () => void;
}) => {
  const { request, onSubmit, onDownloadClick } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const formik = useFormik<IGetInProcessCheckRequest>({
    initialValues: {
      ...request,
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      onSubmit({
        ...values,
        type: 0,
        pageNumber: 1,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      });
    },
  });
  const history = useHistory();
  const searchParams = useSearchParams<IGetInProcessCheckRequest>();

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.inProcessCheck)}
      formik={formik}
      loading={false}
      hideSearch
      renderButtons={
        <>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={onDownloadClick}
            startIcon={<Download />}
            loading={downloading}
            disabled={!canViewAndDownload()}
          >
            {I18nTranslator(translations.export)}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            type="submit"
            color="primary"
            startIcon={<SearchIcon />}
            disabled={!canViewAndDownload()}
          >
            {I18nTranslator(translations.button.search)}
          </LoadingButton>
          {canPerformAndReview() && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/inprocess-check/new")}
              startIcon={<AddIcon />}
            >
              {I18nTranslator(translations.button.addNew)}
            </Button>
          )}
        </>
      }
    >
      <Grid container xs={12} item spacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <ProductCodeSelect
            defaultValue={
              searchParams.get("productId")
                ? {
                    id: searchParams.get("productId")!,
                  }
                : undefined
            }
            onProductSelected={(product) => {
              formik.setFieldValue("productName", product?.description);
              formik.setFieldValue("productCode", product?.productCode);
            }}
            required={false}
          />
        </Grid>
        <Grid xs={3} item>
          <ProductLotSelect
            defaultValue={
              searchParams.get("productLot")
                ? searchParams.get("productLot")!
                : undefined
            }
            productId={formik.values.productId}
            required={false}
          />
        </Grid>
        <Grid xs={3} item>
          <StatusSelect />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const StatusSelect = () => {
  return (
    <FormTextField
      id="status"
      select
      label={I18nTranslator(translations.report.status)}
    >
      <MenuItem value="">
        <Typography
          color="grey"
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          <ClearIcon fontSize="small" />
          {I18nTranslator(translations.inProcessCheckReportDetail.clearStatus)}
        </Typography>
      </MenuItem>
      {INPROCESS_STATUS.map((e, index) => (
        <MenuItem value={e.value} key={index}>
          <Typography color="grey" fontWeight="bold">
            {I18nTranslator(e.title)}
          </Typography>
        </MenuItem>
      ))}
    </FormTextField>
  );
};
